<template>
    <layout-basic>
        <div class="section">
            <div class="container">
                <div class="columns">
                    <div class="column auto">
                        <form
                            id="app"
                            novalidate
                            action="#"
                            enctype="multipart/form-data"
                            method="post"
                            v-on:submit="formSubmit"
                        >
                            <template>
                                <section style="margin-top: 55px">
                                    <b-steps
                                            v-model="activeStep"
                                            type="is-info"
                                            :animated="isAnimated"
                                            :has-navigation="hasNavigation"
                                            :icon-prev="prevIcon"
                                            :icon-next="nextIcon">
                                        <b-step-item label="Product setup" :clickable="isStepsClickable">
                                            <ProductSetup />
                                        </b-step-item>

                                        <b-step-item label="Cart overview" :clickable="isStepsClickable">
                                            <Cart @goToStep="goToStep"/>
                                        </b-step-item>

                                        <b-step-item label="Shipping address" :clickable="isStepsClickable">
                                            <AddressSetup />
                                        </b-step-item>

                                        <b-step-item label="Product files" :clickable="isStepsClickable">
                                            <FileSetup />
                                        </b-step-item>

                                        <b-step-item label="Confirm order" :clickable="isStepsClickable">
                                            <OrderComplete />
                                        </b-step-item>

                                        <b-step-item label="Finish" :clickable="isStepsClickable" disabled>
                                            <Success />
                                        </b-step-item>
                                        <template
                                                slot="navigation"
                                                slot-scope="{previous, next}">
                                            <section style="padding: 1rem;">
                                                <div v-if="orderData.priceCheckErrors.length" class="help is-danger">
                                                    <p v-for="errorMessage in orderData.priceCheckErrors" :key="errorMessage" v-html="errorMessage"></p>
                                                </div>
                                                <input v-if="activeStep == 4" type="submit" name="submit"
                                                       class="button is-info is-pulled-right"
                                                       :disabled="orderData.priceCheckErrors.length > 0"
                                                       value="Order">
                                                <b-button
                                                        v-if="activeStep < 4"
                                                        icon-pack="fas"
                                                        class="button is-info is-pulled-right"
                                                        :disabled="next.disabled || (activeStep >= 1 && Boolean(orderData.priceCheckErrors.length))"
                                                        native-type="submit"
                                                        tag="input"
                                                        :value="activeStep === 0 && orderData.editMode ? 'Save & Next' : 'Next'"
                                                >
                                                    Next
                                                </b-button>

                                                <b-button
                                                        v-if="activeStep === 0 && cartProducts.length > 0"
                                                        icon-pack="fas"
                                                        class="button is-info is-pulled-right"
                                                        tag="input"
                                                        v-on:click="goToCart"
                                                        value="Go to cart"
                                                        style="margin-right: 30px;"
                                                >
                                                    Go to cart
                                                </b-button>

                                                <b-button
                                                        v-if="activeStep > 1 && activeStep < 5"
                                                        icon-pack="fas"
                                                        class="button is-info is-pulled-right"
                                                        :disabled="previous.disabled"
                                                        @click.prevent="previous.action"
                                                        style="margin-right: 30px;">
                                                    Back
                                                </b-button>
                                            </section>
                                        </template>
                                    </b-steps>
                                </section>
                            </template>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </layout-basic>
</template>

<script>
import _ from 'lodash'
import store from '@/store'
import EventBus from '@/event-bus'

import ProductSetup from '@/components/order-create/ProductSetup'
import AddressSetup from '@/components/order-create/AddressSetup'
import FileSetup from '@/components/order-create/FileSetup'
import OrderComplete from '@/components/order-create/OrderComplete'
import Success from '@/components/order-create/Success'
import Cart from '@/components/order-create/Cart'
import BField from 'buefy/src/components/field/Field'

import Cloudprinter from '@/services/CloudprinterService'
import NotificationService from '@/services/NotificationService'
import { CartService } from '../services/CartService'
import AWS from '../services/AWSService'

import LayoutBasic from '@/layouts/LayoutBasic'
import AddressMixin from '@/mixins/AddressMixin'

import { Validator } from 'vee-validate'
import fileValidator from '@/validators/file'
import {getFileTypesForCartItem, getUniqueFileTypes} from '../utils/file-types';

export default {
  name: 'OrderCreate',
  mixins: [AddressMixin],
  components: {
    Success,
    BField,
    LayoutBasic,
    ProductSetup,
    AddressSetup,
    FileSetup,
    OrderComplete,
    Cart
  },
  data () {
    return {
      activeStep: 0,
      isAnimated: true,
      hasNavigation: true,
      customNavigation: true,
      prevIcon: 'chevron-left',
      nextIcon: 'chevron-right',
      isStepsClickable: false,
      orderData: {},
      cartProducts: [],
      cpCountries: [],
      cpProductInfo: {},
      quoteError: null
    }
  },

  created () {
    this.orderData = this.$store.getters['cpOrder/allData']
    this.cartProducts = this.$store.getters['cart/products']

    if (this.cartProducts.length > 0) {
      this.activeStep = 1
      this.$store.dispatch('cpOrder/priceCheckForCartItems')

      this.getCountryList().then(() => {
        this.getStates()
        this.orderData.shipping_address.state = this.$store.getters['cart/state']
      })
    }

    this.$validator.extend('zip_required', {
      getMessage: field => 'The zip field is required',
      validate: (value, [country]) => {
        if (value) {
          return true
        }

        return !this.zipIsRequired(country)
      }
    })

    Validator.extend('file_exists', fileValidator)
  },

  mounted () {
    EventBus.$on('ABORT_ORDER_SUBMISSION', () => {
      AWS.abortUploading()
    })
  },

  watch: {
    activeStep: function (value) {
      if (value === 4) {
        EventBus.$emit('REQUEST_ORDER_QUOTE')
      }
    },
    cartProducts: function (cartProducts) {
      if (cartProducts.length === 0) {
        this.activeStep = 0
      }
    }
  },

  methods: {
    uploadAllFilesToS3: function () {
      let uploadPromises = []
      const countFiles = this.$store.getters['cart/countFilesToUpload']

      if (countFiles === 0) {
        return Promise.all(uploadPromises)
      }
      AWS.configureAWS(this.$store)
      this.$store.dispatch('progress/start', countFiles)

      for (let [index, product] of this.cartProducts.entries()) {
        for (let file of getFileTypesForCartItem(product)) {
          if (['link', 'original_file', 'dropbox'].indexOf(product.file_storage[file.type]) !== -1) {
            continue
          }

          let fileElement = document.getElementById('file-' + file.type + '-' + index)
          let fileContext = fileElement.files[0]
          let fileName = this.generateRandomString() + '-' + file.type + '-' + index + '.' + file.format
          product.cp_files.link[file.type] = AWS.getS3FileUrl(fileName)
          uploadPromises.push(AWS.uploadFile(fileContext, fileName))
        }
      }

      return Promise.all(uploadPromises)
    },

    generateRandomString: function () {
      return new Date().getTime()
    },

    formSubmit: function (event) {
      event.preventDefault()
      if (this.activeStep === 0) {
        if (this.orderData.type === 'pod' && !getUniqueFileTypes(this.orderData.cpProductInfo, this.orderData.cp_product_options).length) {
          this.orderData.priceCheckErrors = ['At least one print area option should be selected. Don\'t hesitate to get in touch with the support for more details.']

          return
        }
        this.$validator.validate('product_form.*').then((result) => {
          if (result) {
            const cartService = new CartService()
            this.$store.dispatch('loader/show')
            let priceCheckPromise
            if (this.orderData.editMode === true) {
              cartService.editProduct(this.orderData.editProductIndex)
              priceCheckPromise = this.$store.dispatch('cpOrder/priceCheckForCartItems')
            } else {
              priceCheckPromise = this.$store.dispatch('cpOrder/priceCheckAfterAddNewProduct')
            }

            priceCheckPromise.then((response) => {
              // Todo. Remove this condition when CPMAIN-112 is done.
              if (response.data && !response.data.error) {
                // Add product to cart
                if (this.orderData.editMode !== true) {
                  cartService.addProductToCart()
                }

                // Disable edit mode
                store.dispatch('cpOrder/disableEditMode')

                this.activeStep++
              }
            }).finally(() => {
              this.$store.dispatch('loader/hide')
            })
          }
        })
      }
      if (this.activeStep === 1) {
        this.activeStep++
      } else if (this.activeStep === 2) {
        this.$validator.validate('shipping_address_form.*').then((result) => {
          if (result) {
            this.activeStep++
          }
        })
      } else if (this.activeStep === 3) {
        this.$validator.validate('file_form.*').then((result) => {
          if (result) {
            this.activeStep++
          }
        })
      } else if (this.activeStep === 4) {
        this.$validator.validate('shipping_method_form.*').then((result) => {
          if (result && !this.quoteError) {
            this.$store.dispatch('loader/show')
            this.uploadAllFilesToS3().then(() => {
              this.orderPost()
                .then(() => {
                  this.$store.dispatch('cart/clearCart')
                  this.$store.dispatch('cpOrder/clearProductData')
                  this.activeStep = 5
                })
                .catch(error => {
                  NotificationService.warning('Something went wrong with order creation. ' + error.response.data.error.info)
                })
                .finally(() => {
                  this.$store.dispatch('loader/hide')
                })
            }).catch((error) => {
              switch (error.code) {
                case 'RequestAbortedError': {
                  break
                }
                case 'NetworkingError': {
                  NotificationService.warning('Please, check the internet connection and try again.')
                  break
                }
                default: {
                  NotificationService.warning('Something went wrong with file uploading.')
                }
              }

              this.$store.dispatch('loader/hide')
            }).finally(() => {
              this.$store.dispatch('progress/finish')
            })
          }
        })
      }
    },

    orderPost: function () {
      let orderItems = this.$store.getters['cart/itemsForOrderSubmission']

      const shippingAddress = this.$store.getters['cpOrder/shippingAddress']
      let orderData = {
        'reference': 'qo-' + this.orderData.order_reference.replace(' ', '') + '-' + new Date().getTime(),
        'mode': this.orderData.mode,
        'email': store.getters['cpAuth/currentUser'].user.email,
        'addresses': [shippingAddress],
        'items': orderItems
      }

      return Cloudprinter.orderCreate(orderData)
    },

    goToCart: function () {
      let confirmationRequired = false

      if (this.orderData.editMode) {
        confirmationRequired = this.productIsChangedOnEdit()
      } else {
        confirmationRequired = Boolean(this.orderData.cp_product_reference)
      }

      let goToFirstStep = () => {
        this.activeStep = 1
        store.dispatch('cpOrder/disableEditMode')
      }

      if (confirmationRequired) {
        this.$buefy.dialog.confirm({
          message: 'Are you sure you want to go to the cart? The changes you made won`t be saved.',
          confirmText: 'Go to cart',
          type: 'is-warning',
          hasIcon: true,
          onConfirm: () => {
            this.revertTheCountryData()
            goToFirstStep()
          }
        })
      } else {
        goToFirstStep()
      }
    },

    productIsChangedOnEdit: function () {
      const cartProduct = this.cartProducts[this.orderData.editProductIndex]
      const beforeEditSnapshot = JSON.stringify({ ...cartProduct.cp_product_options }) +
        cartProduct.quantity +
        this.$store.getters['cart/country'] +
        this.$store.getters['cart/state']

      const currentSnapshot = JSON.stringify({ ...this.orderData.cp_product_options }) +
        this.orderData.quantity +
        this.orderData.shipping_address.country +
        this.orderData.shipping_address.state

      return beforeEditSnapshot !== currentSnapshot
    },

    goToStep: function (step) {
      this.activeStep = step
    },

    revertTheCountryData: function () {
      this.orderData.shipping_address.country = this.$store.getters['cart/country']
      this.orderData.shipping_address.state = this.$store.getters['cart/state']
    }
  }
}
</script>
